import api from "@/api";
import i18n from "@/i18n";
import { createLatestRequestGate, parseApiError, parseCollectionAuthorization } from "@/helpers/teamAuthorization";

const { t } = i18n.global;
const teamsRequestGate = createLatestRequestGate();

export default {
  state: {
    teams: [],
    collectionAuthorization: null,
    collectionError: null,
  },
  getters: {
    teams: (state) => state.teams,
    teamCollectionAuthorization: (state) => state.collectionAuthorization,
    teamCollectionError: (state) => state.collectionError,
  },
  mutations: {
    setTeams: (state, data) => {
      state.teams = data;
    },
    setTeamCollectionAuthorization: (state, data) => {
      state.collectionAuthorization = data;
    },
    setTeamCollectionError: (state, data) => {
      state.collectionError = data;
    },

  },
  actions: {
    getTeams: async ({ commit, rootGetters }) => {
      const requestKey = `teams:${rootGetters.loggedUser?.id || "anonymous"}`;
      const { signal } = teamsRequestGate.start(requestKey);
      commit("setTeams", []);
      commit("setTeamCollectionAuthorization", null);
      commit("setTeamCollectionError", null);
      try {
        const response = await api({ requiresAuth: true }).get("/v1/teams", { signal });
        if (!teamsRequestGate.isActive(requestKey)) return;
        const { data } = response.data;
        commit("setTeams", data);
        commit("setTeamCollectionAuthorization", parseCollectionAuthorization(response));
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !teamsRequestGate.isActive(requestKey)) return;
        commit("setTeamCollectionError", parseApiError(err));
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
      }
    },

    getTeamAgentsOld: async ({ commit }, id) => {
      try {
        const response = await api({ requiresAuth: true }).get(`/v1/teams/${id}/agents`);
        const { data } = response.data;
        return data;
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
        return [];
      }
    },

    getTeamAgents: async ({ commit }, payload = {}) => {
      try {
        const teamIds = Array.isArray(payload?.teamIds)
          ? payload.teamIds
          : Array.isArray(payload?.teams)
            ? payload.teams
            : payload?.teamId !== undefined && payload.teamId !== null
              ? [payload.teamId]
              : typeof payload === "number" || typeof payload === "string"
                ? [payload]
                : [];

        const models = Array.isArray(payload?.models)
          ? payload.models
          : payload?.model !== undefined && payload.model !== null
            ? [payload.model]
            : [];

        const queryString = [
          ...teamIds.map((teamId) => `teams[]=${encodeURIComponent(teamId)}`),
          ...models.map((model) => `models[]=${encodeURIComponent(model)}`),
        ].join("&");

        const response = await api({ requiresAuth: true }).get(
          queryString ? `/v1/partials/team-agents?${queryString}` : "/v1/partials/team-agents"
        );
        const { data } = response.data;
        return data;
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
        return [];
      }
    },

    deleteTeam: async ({ commit, dispatch }, teamId) => {
      commit("setUsersLoading", true);
      try {
        await api({ requiresAuth: true }).delete(`/v1/teams/${teamId}`);
        await dispatch("getTeams");
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
      } finally {
        commit("setTeamsLoading", false);
      }
    },

    createTeam: (
      { commit, dispatch },
      {
        name,
        fromTeamId,
        owner,
        finma_nr,
        mandate_type_ids,
        zip,
        city,
        street,
        number,
        file

      }
    ) =>
      new Promise((resolve, reject) => {
        const payload = {
          name: name,
          fromTeamId,
          owner_id: owner.id,
          finma_nr: finma_nr,
          zip,
          city,
          street,
          number,
          file
        };

        // Include mandate_type_ids if provided
        if (mandate_type_ids !== undefined) {
          payload.mandate_type_ids = mandate_type_ids;
        }

        api({ requiresAuth: true })
          .post("/v1/teams", payload)
          .then(
            async (response) => {
              const { data } = response.data;
              commit("setTeamsLoading", true);
              await dispatch("getTeams");
              resolve(data);
              commit("setTeamsLoading", false);

              if (file) {
                await dispatch("addTeamPhoto", {
                  teamId: data.id,
                  file,
                });
              }
            },
            (err) => {
              commit("setTeamsLoading", false);
              commit("setToast", {
                display: true,
                type: "error",
                message: t("messages.somethingWentWrong"),
              });
              reject(err);
            }
          );
      }),

    updateTeam: (
      { commit, dispatch },
      {
        teamId,
        name,
        owner,
        finma_nr,
        mandate_type_ids,
        street,
        number,
        zip,
        city

      }
    ) =>
      new Promise((resolve, reject) => {
        const payload = {
          name: name,
          owner_id: owner.id,
          finma_nr: finma_nr,
          teamId: teamId,
          street,
          number,
          city,
          zip
        };

        // Include mandate_type_ids if provided
        if (mandate_type_ids !== undefined) {
          payload.mandate_type_ids = mandate_type_ids;
        }

        api({ requiresAuth: true })
          .patch("/v1/teams/" + teamId, payload)
          .then(
            async (response) => {


              const { data } = response.data;
              commit("setTeamsLoading", true);
              await dispatch("getTeams");
              resolve(data);
              commit("setTeamsLoading", false);
            },
            (err) => {
              commit("setTeamsLoading", false);
              commit("setToast", {
                display: true,
                type: "error",
                message: t("messages.somethingWentWrong"),
              });
              reject(err);
            }
          );
      }),

    addTeamPhoto: async ({ commit }, { teamId, file }) => {
      const formData = new FormData();
      formData.append("photo", file);
      try {
        return await api({ requiresAuth: true }).post(
          `/v1/teams/${teamId}/files`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
        throw err;
      }
    },
  },


};
